<template>
  <div
    name="rb-select"
    :style="cssVars"
  >
    <RbInput
      v-model="input"
      class="insideClick"
      :label="props.label"
      :placeholder="props.placeholder"
      :color="props.color"
      :primary="props.primary"
      :secondary="props.secondary"
      :data-select-identifier="props.label"
      :disabled="props.disabled"
      :required="props.required"
      @click="openList"
      @keyup="filterData"
      @focusin="openList"
      @focusout="focusOut"
    />
    <ul
      v-if="autocompleteOpen && props.loading"
      class="insideClick"
      :class="{ 'top': autocompletePositionTop }"
    >
      <li>
        Buscando opções...
      </li>
    </ul>
    <ul
      v-if="autocompleteOpen && !props.loading"
      class="insideClick"
      :class="{ 'top': autocompletePositionTop }"
    >
      <li v-if="!filteredData.length">
        Nenhuma opção disponível
      </li>
      <li
        v-for="item in filteredData"
        :key="item[props.config.key]"
        @click="selectItem(item)"
        class="insideClick"
      >
        {{ item[props.config.title] }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { withDefaults, ref, watchEffect, onBeforeMount, reactive, computed, watch } from 'vue';
import { getRootElement } from '@/lib/Utils';
import RbInput from './RbInput.vue';

export interface ISelectConfig {
  key: string,
  title: string
}

const emits = defineEmits(['update:modelValue']);
const props = withDefaults(defineProps<{
  modelValue: any,
  config: ISelectConfig,
  data: Array<any>,
  label: string,
  color?: string,
  placeholder?: string,
  primary?: boolean,
  secondary?: boolean,
  disabled?: boolean,
  required?: boolean,
  loading?: boolean
}>(), {
  placeholder: '',
  color: 'rgba(0, 0, 0, 0.6)',
  loading: false
});

const input = ref('');
const select = ref<Object>();
const autocompleteOpen = ref(false);
const autocompletePositionTop = ref(false);
const filteredData: any = ref([]);

onBeforeMount(handleInicialOption);
watchEffect(handleInputValue);

const cssVars = computed(() => {
  return {
    ['--ul-top']: list.top + 'px',
    ['--ul-max-width']: list.maxWidth + 'px',
    ['--ul-bottom']: list.bottom + 'px',
  };
});

const list = reactive({
  top: 0,
  maxWidth: 0,
  bottom: 0
});

function handleInicialOption() {
  let option;
  if (props.modelValue) {
    option = props.data.find(option => option[props.config.key] === props.modelValue[props.config.key]);
  }

  setOption(option);
}

function handleInputValue() {
  input.value = props.modelValue ? props.modelValue[props.config.title] : '';
}

function setOption(option?: any) {
  select.value = option;
  emits('update:modelValue', select.value);
  handleInputValue();
}

function openList(event: any) {
  handleActiveClass(event);
  handleListPosition(event);
  filterData({ target: { value: input.value } });
  autocompleteOpen.value = true;
  window.addEventListener('click', windowClick);
}

function handleListPosition(event: any) {
  const inputTarget = event.composedPath()[0];
  list.maxWidth = inputTarget.offsetWidth;
  list.top = inputTarget.getBoundingClientRect().top + inputTarget.offsetHeight + 1;
  list.bottom = inputTarget.getBoundingClientRect().bottom + 126;

  const itemHeight = 48;
  const maxItemQuantity = 4;
  const windowHeight = getRootElement().offsetHeight;

  const input = {
    heigthOptions: (props.data.length > maxItemQuantity ? maxItemQuantity : props.data.length) * itemHeight,
    position: inputTarget.getBoundingClientRect().bottom,
    distanceForBottom: windowHeight - inputTarget.getBoundingClientRect().bottom + 16
  };

  autocompletePositionTop.value = input.distanceForBottom < input.heigthOptions && input.position > input.distanceForBottom;
}

function windowClick(event: any) {
  if (clickOutside(event)) {
    closeOptions();
  }
}

function focusOut() {
  setTimeout(closeOptions, 200);
}

function closeOptions() {
  window.removeEventListener('click', windowClick);
  toggleOptions(false);
  if (input.value !== '') {
    setOption(props.modelValue);
  } else {
    setOption();
  }
}

function handleActiveClass(event: any) {
  const root = getRootElement();
  const target = event.composedPath()[0];

  root.querySelectorAll('.insideClick input.active').forEach((input: any) => input.classList.remove('active'));

  if (target.tagName === 'INPUT') {
    target.classList.add('active');
  }
}

function clickOutside(event: any): boolean {
  const target = event.composedPath()[0];
  const root = getRootElement();
  if (target.tagName === 'INPUT') {
    if (target.parentElement.dataset.selectIdentifier != props.label) {
      return true;
    }
    if (target.classList.contains('active')) {
      return false;
    }
  }
  return !target.classList.contains('insideClick') && !root.querySelector('.insideClick input')?.contains(target);
}

function toggleOptions(value: boolean | null = null) {
  autocompleteOpen.value = value ?? !autocompleteOpen.value;
}

function selectItem(item: any) {
  toggleOptions(false);
  setOption(item);
}

function filterData(event: any) {
  const value = event.target.value ?? '';
  filteredData.value = filter(value);
}

function filter(value: string) {
  const filterValue = normalizeText(value);
  return props.data.filter(option =>
    normalizeText(option[props.config.title]).match(filterValue)).sort((a, b): number => {
    if (normalizeText(a[props.config.title]) < normalizeText(b[props.config.title])) {
      return -1;
    }

    if (normalizeText(a[props.config.title]) > normalizeText(b[props.config.title])) {
      return 1;
    }

    return 0;
  });
}

function normalizeText(text: string = ''): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

watch(() => props.loading, () => {
  if(props.loading === false) {
    filteredData.value = filter('');
  }
});
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

div[name="rb-select"] {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  &:has(ul.top) {
    flex-direction: column-reverse;
  }

  ul,
  ul li {
    margin: 0;
    padding: 0;
    text-indent: 0;
  }

  ul {
    position: fixed;
    z-index: 9999;
    top: var(--ul-top);
    width: 100%;
    max-width: var(--ul-max-width);
    max-height: 192px;
    overflow-x: auto;
    border-radius: 3px;
    background-color: white;
    box-shadow: 0px 1px 10px -1px var(--shadow);

    &.top {
      top: unset;
      bottom: var(--ul-bottom);
    }

    li {
      display: flex;
      position: relative;
      align-items: center;
      justify-content: flex-start;
      overflow: hidden;
      padding: 0;
      padding-left: 16px;
      padding-right: 16px;
      cursor: pointer;
      min-height: 48px;

      &:hover {
        background-color: var(--input);
      }
    }
  }
}
</style>
